import { useId } from 'react'

import { ChartDataTable, ChartYGrid, useChartCursor } from './chartParts'
import { formatValue, type FormatOptions, type ValueFormat } from './format'

export interface TrendChartPoint {
  label: string
  current: number
  previous: number | null
}

export interface TrendChartProps {
  /** The chart's accessible name. */
  title: string
  points: readonly TrendChartPoint[]
  format: ValueFormat
  formatOptions?: FormatOptions
  currentLabel: string
  previousLabel?: string | null
  height?: number
}

const WIDTH = 640
const PAD = { left: 56, right: 12, top: 12, bottom: 28 }
const Y_TICKS = 4
const MAX_X_LABELS = 7

export function niceMax(value: number): number {
  if (value <= 0) return 1
  const magnitude = 10 ** Math.floor(Math.log10(value))
  const scaled = value / magnitude
  const nice = scaled <= 1 ? 1 : scaled <= 2 ? 2 : scaled <= 2.5 ? 2.5 : scaled <= 5 ? 5 : 10
  return nice * magnitude
}

/**
 * A line chart of the current period over the comparison period (dashed).
 * Pointer hover and arrow keys both move a readout, announced politely; the
 * full series is also available as a visually hidden table.
 */
export function TrendChart({
  title,
  points,
  format,
  formatOptions,
  currentLabel,
  previousLabel,
  height = 220,
}: TrendChartProps) {
  const innerWidth = WIDTH - PAD.left - PAD.right
  const cursor = useChartCursor(points.length, WIDTH, (position) =>
    Math.round(((position - PAD.left) / innerWidth) * (points.length - 1)),
  )
  const { active, readoutId } = cursor
  const gradientId = `trend-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`

  if (points.length === 0) return null

  const innerHeight = height - PAD.top - PAD.bottom
  const values = points.flatMap((point) => (point.previous === null ? [point.current] : [point.current, point.previous]))
  const max = niceMax(Math.max(...values))
  const x = (index: number) => PAD.left + (points.length === 1 ? innerWidth / 2 : (index / (points.length - 1)) * innerWidth)
  const y = (value: number) => PAD.top + innerHeight - (value / max) * innerHeight
  const fmt = (value: number, compact = false) => formatValue(value, format, { ...formatOptions, compact })

  const line = (pick: (point: TrendChartPoint) => number | null) => {
    let path = ''
    let pen = false
    points.forEach((point, index) => {
      const value = pick(point)
      if (value === null) {
        pen = false
        return
      }
      path += `${pen ? 'L' : 'M'}${x(index).toFixed(1)},${y(value).toFixed(1)} `
      pen = true
    })
    return path.trim()
  }
  const currentPath = line((point) => point.current)
  const previousPath = previousLabel ? line((point) => point.previous) : ''
  const areaPath = `${currentPath} L${x(points.length - 1).toFixed(1)},${PAD.top + innerHeight} L${x(0).toFixed(1)},${PAD.top + innerHeight} Z`

  const labelEvery = Math.max(1, Math.ceil(points.length / MAX_X_LABELS))
  const activePoint = active === null ? null : points[active]

  return (
    <figure className="ci-trend">
      <div className="ci-trend__plot" aria-label={`${title}. Use the arrow keys to read values.`} {...cursor.plotProps}>
        <svg
          viewBox={`0 0 ${WIDTH} ${height}`}
          className="ci-trend__svg"
          aria-hidden="true"
          focusable="false"
          {...cursor.svgProps}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="var(--ci-accent)" stopOpacity="0.32" />
              <stop offset="100%" stopColor="var(--ci-accent)" stopOpacity="0" />
            </linearGradient>
          </defs>
          <ChartYGrid max={max} ticks={Y_TICKS} left={PAD.left} right={WIDTH - PAD.right} y={y} label={(value) => fmt(value, true)} />
          {points.map((point, index) =>
            index % labelEvery === 0 || index === points.length - 1 ? (
              <text key={point.label + index} className="ci-trend__x" x={x(index)} y={height - 8} textAnchor="middle">
                {point.label}
              </text>
            ) : null,
          )}
          <path d={areaPath} fill={`url(#${gradientId})`} />
          {previousPath ? <path d={previousPath} className="ci-trend__previous" /> : null}
          <path d={currentPath} className="ci-trend__current" />
          {activePoint && active !== null ? (
            <g className="ci-trend__cursor">
              <line x1={x(active)} x2={x(active)} y1={PAD.top} y2={PAD.top + innerHeight} />
              <circle cx={x(active)} cy={y(activePoint.current)} r={4} />
            </g>
          ) : null}
        </svg>
        <div
          id={readoutId}
          className="ci-trend__readout"
          aria-live="polite"
          data-visible={activePoint ? 'true' : 'false'}
          style={active !== null ? { left: `${(x(active) / WIDTH) * 100}%` } : undefined}
        >
          {activePoint ? (
            <>
              <strong>{activePoint.label}</strong>
              <span>
                {currentLabel}: {fmt(activePoint.current)}
              </span>
              {previousLabel && activePoint.previous !== null ? (
                <span>
                  {previousLabel}: {fmt(activePoint.previous)}
                </span>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
      <figcaption className="ci-trend__legend">
        <span className="ci-trend__key" data-series="current">
          {currentLabel}
        </span>
        {previousLabel ? (
          <span className="ci-trend__key" data-series="previous">
            {previousLabel}
          </span>
        ) : null}
      </figcaption>
      <ChartDataTable
        title={title}
        columns={previousLabel ? ['Point', currentLabel, previousLabel] : ['Point', currentLabel]}
        rows={points.map((point, index) => ({
          key: `${point.label}-${index}`,
          name: point.label,
          cells: previousLabel ? [fmt(point.current), point.previous === null ? '—' : fmt(point.previous)] : [fmt(point.current)],
        }))}
      />
    </figure>
  )
}
